import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";

const inputCls = "w-full bg-white border border-[#D9E0DC] rounded-xl px-4 py-3 text-base outline-none transition-shadow focus:border-[var(--sj-primary)] focus:ring-2 focus:ring-[#0B5E4E]/15";
const labelCls = "block text-sm font-semibold mb-1.5";

const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className={labelCls} style={{ color: "var(--sj-text-2)" }}>{label}</label>
    {children}
  </div>
);

export default function CellResponseForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", postcode: "", contact_permission: false, willing_to_host: false, website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/public/cell/response", form); setDone(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again."); }
    setLoading(false);
  };

  return (
    <div className="min-h-[100dvh] px-4 py-8 sm:py-12" style={{ background: "var(--sj-bg)" }}>
      <div className="max-w-2xl mx-auto space-y-5 sj-fade-up" data-testid="cell-response-form-page">
        <div className="sj-hero px-7 py-8 sm:px-9">
          <div className="sj-eyebrow">Streams of Joy Erith London</div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold mt-2 leading-tight">Cell Group Response Form</h1>
          <p className="text-sm sm:text-base mt-2 text-white/85">Tell us where you live so we can connect you with a cell group near you.</p>
        </div>

        <div className="bg-white rounded-3xl border p-6 sm:p-7 shadow-[0_8px_30px_rgba(6,59,48,0.06)]" style={{ borderColor: "var(--sj-border)" }}>
          {done ? (
            <div className="text-center py-8" data-testid="cell-response-success">
              <CheckCircle2 className="h-12 w-12 mx-auto" style={{ color: "var(--sj-primary)" }} />
              <h2 className="font-head text-2xl font-bold mt-3">Thank you, {form.name.split(" ")[0]}!</h2>
              <p className="mt-2 text-sm" style={{ color: "var(--sj-text-3)" }}>Your response has been received. A cell group leader will be in touch about a group near you.</p>
              <Link to="/login" data-testid="cell-response-back-login" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <Field id="cr-name" label="Full name"><input id="cr-name" data-testid="cell-response-name" required minLength={2} value={form.name} onChange={upd("name")} autoComplete="name" className={inputCls} /></Field>
              <Field id="cr-phone" label="Phone number"><input id="cr-phone" data-testid="cell-response-phone" type="tel" inputMode="tel" value={form.phone} onChange={upd("phone")} autoComplete="tel" className={inputCls} /></Field>
              <Field id="cr-email" label="Email address"><input id="cr-email" data-testid="cell-response-email" type="email" value={form.email} onChange={upd("email")} autoComplete="email" className={inputCls} /></Field>
              <Field id="cr-postcode" label="Postcode"><input id="cr-postcode" data-testid="cell-response-postcode" required value={form.postcode} onChange={upd("postcode")} placeholder="Example: DA8 1AB" autoComplete="postal-code" className={inputCls + " uppercase placeholder:normal-case"} /></Field>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={upd("website")} className="hidden" aria-hidden="true" />
              <label className="flex items-start gap-3 text-[15px] cursor-pointer" style={{ color: "var(--sj-text-2)" }}>
                <input data-testid="cell-response-permission" type="checkbox" checked={form.contact_permission} onChange={upd("contact_permission")} className="mt-1 h-4 w-4 accent-[#0B5E4E] shrink-0" />
                I give permission for Streams of Joy Erith London to contact me about cell groups.
              </label>
              <label className="flex items-start gap-3 text-[15px] cursor-pointer" style={{ color: "var(--sj-text-2)" }}>
                <input data-testid="cell-response-host" type="checkbox" checked={form.willing_to_host} onChange={upd("willing_to_host")} className="mt-1 h-4 w-4 accent-[#0B5E4E] shrink-0" />
                I am open to discussing hosting a cell group.
              </label>
              <button data-testid="cell-response-submit" disabled={loading || !form.contact_permission} className="w-full rounded-xl py-3.5 font-semibold text-white transition-[filter,opacity] hover:brightness-110 disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>
                {loading ? "Submitting…" : "Submit response"}
              </button>
              {!form.contact_permission && <p className="text-xs text-center -mt-2" style={{ color: "var(--sj-text-3)" }}>Please tick the contact permission box so we can reach you.</p>}
            </form>
          )}
        </div>
        <p className="text-center text-xs" style={{ color: "var(--sj-text-3)" }}>
          Already a member? <Link to="/login" data-testid="cell-response-login-link" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Sign in</Link> · <a href="/privacy" data-testid="cell-response-privacy" className="hover:underline">Privacy &amp; Data Protection Policy</a>
        </p>
      </div>
    </div>
  );
}
